import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import isEmpty from 'lodash.isempty';
import { useState } from 'react';
import Alert from '../../../components/Alert';
import { useNotificationStore } from '../../../components/notificationStore';
import { useUserStore } from '../../../components/userStore';
import EnrolleeModal from './EnrolleeModal';

const reformatDate = (origDateStr) => {
	const [year, month, day] = origDateStr.split('-');
	return `${month}/${day}/${year}`;
};

const MemberSearch = () => {
	const { user } = useUserStore();
	const { showSuccess, showError } = useNotificationStore();
	const [fName, setFName] = useState('');
	const [lName, setLName] = useState('');
	const [dob, setDob] = useState('');
	const [isEnrolleeModalOpen, setIsEnrolleeModalOpen] = useState(false);
	const [selectedEnrollee, setSelectedEnrollee] = useState(null);

	const searchQuery = useQuery({
		queryKey: ['ilos-member-file-search', fName, lName, dob],
		queryFn: async () => {
			const { data } = await axios.get(`/coords/ilos-member-file-search`, {
				params: {
					first_name: isEmpty(fName) ? null : fName,
					last_name: isEmpty(lName) ? null : lName,
					dob: isEmpty(dob) ? null : reformatDate(dob),
				},
			});
			return data;
		},
		enabled: false,
		retry: false,
	});

	const addEnrolleeMutation = useMutation({
		mutationFn: async (newEnrollee) => {
			setIsEnrolleeModalOpen(false);
			const { data } = await axios.post(
				`/coord_agencies/${user.coord_agency_id}/add-ilos-member`,
				newEnrollee,
			);
			return data;
		},
		onSuccess: () => {
			if (searchQuery.isFetched) {
				searchQuery.refetch();
			}
			setSelectedEnrollee(null);
			showSuccess('Enrollee added successfully');
		},
		onError: (error) => {
			showError(
				`Error adding enrollee: ${error.response?.data?.error?.message || error.message}`,
			);
		},
	});

	const handleSearch = () => {
		searchQuery.refetch();
	};

	const handleViewMember = (enrollee) => {
		setSelectedEnrollee(enrollee);
		setIsEnrolleeModalOpen(true);
	};

	const handleCloseEnrolleeModal = () => {
		setIsEnrolleeModalOpen(false);
		setSelectedEnrollee(null);
	};

	const handleAddEnrollee = () => {
		setSelectedEnrollee(null);
		setIsEnrolleeModalOpen(true);
	};

	return (
		<>
			<div className="flex flex-row items-start space-between">
				<h1 className="flex-1">Member Search</h1>
				<button className="btn btn-primary" onClick={handleAddEnrollee}>
					Add Enrollee
				</button>
			</div>
			<p>Search uploaded members file</p>
			<div className="flex flex-row items-end gap-4">
				<div className="form-control">
					<label className="label">First Name</label>
					<input
						type="text"
						className="input input-bordered"
						value={fName}
						onChange={({ target }) => setFName(target.value)}
						autoComplete="off"
					/>
				</div>
				<div className="form-control">
					<label className="label">Last Name</label>
					<input
						type="text"
						className="input input-bordered"
						value={lName}
						onChange={({ target }) => setLName(target.value)}
						autoComplete="off"
					/>
				</div>
				<div className="form-control">
					<label className="label">Date of Birth</label>
					<input
						type="date"
						className="input input-bordered"
						value={dob}
						onChange={({ target }) => setDob(target.value)}
						autoComplete="off"
					/>
				</div>
				<button
					className="btn btn-primary"
					onClick={handleSearch}
					disabled={
						searchQuery.isFetching ||
						(isEmpty(fName) && isEmpty(lName) && isEmpty(dob))
					}
				>
					{searchQuery.isFetching && (
						<span className="loading loading-spinner"></span>
					)}
					Search
				</button>
			</div>
			{searchQuery.isError && (
				<div className="mt-4">
					<Alert message={searchQuery.error?.response?.data?.error?.message} />
				</div>
			)}
			{searchQuery.isFetched && (
				<table className="table mt-4 table-zebra table-sm">
					<thead>
						<tr>
							<td>First Name</td>
							<td>Last Name</td>
							<td>DOB</td>
							<td>Member ID</td>
						</tr>
					</thead>
					<tbody>
						{searchQuery.data.map((member) => (
							<tr key={member.medicaid_unique_id}>
								<td>{member.first_name}</td>
								<td>{member.last_name}</td>
								<td>{member.dob}</td>
								<td>{member.medicaid_unique_id}</td>
								<td>
									<button
										className="btn btn-primary btn-xs"
										onClick={() => handleViewMember(member)}
									>
										View
									</button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
			<EnrolleeModal
				isOpen={isEnrolleeModalOpen}
				enrollee={selectedEnrollee}
				onClose={handleCloseEnrolleeModal}
				onAddEnrollee={addEnrolleeMutation.mutate}
			/>
		</>
	);
};

export default MemberSearch;
